import { Chip, IconButton, Tooltip } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid-pro";
import TruncateAwareTooltip from "src/components/general/TruncationAwareTooltip/TruncationAwareTooltip";
import type { ApiToken } from "src/queries/auth/tokens.types";
import { MoreVertIcon } from "@ui-icons";
import type { TokenStatus } from "../../SystemSettingsTokenManagement.utils";
import {
  formatRelativeTokenTime,
  getTokenStatus,
  statusConfig,
} from "../../SystemSettingsTokenManagement.utils";

const chipEllipsisSx = {
  maxWidth: "100%",
  "& .MuiChip-label": {
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
} as const;

export function getTokenManagementColumns(
  onOpenMenu: (el: HTMLElement, token: ApiToken) => void
): GridColDef<ApiToken>[] {
  return [
    {
      field: "label",
      headerName: "Label",
      flex: 28,
      minWidth: 120,
      sortable: true,
      renderCell: ({ row }) =>
        row.label ? (
          <Tooltip title={row.label} arrow>
            <span className="block truncate">{row.label}</span>
          </Tooltip>
        ) : (
          <span className="block truncate text-sm text-text-secondary">
            Unlabeled
          </span>
        ),
    },
    {
      field: "token_type",
      headerName: "Type",
      flex: 10,
      minWidth: 72,
      sortable: true,
      renderCell: ({ row }) => (
        <Chip
          label={row.token_type === "agent" ? "Agent" : "API"}
          color={row.token_type === "agent" ? "secondary" : "primary"}
          size="small"
          variant="outlined"
          sx={chipEllipsisSx}
        />
      ),
    },
    {
      field: "read_only",
      headerName: "Access",
      flex: 10,
      minWidth: 92,
      sortable: true,
      renderCell: ({ row }) => (
        <Chip
          label={row.read_only ? "Read-only" : "Read-write"}
          color={row.read_only ? "warning" : "default"}
          size="small"
          variant="outlined"
          sx={chipEllipsisSx}
        />
      ),
    },
    {
      field: "created_at",
      headerName: "Created",
      flex: 14,
      minWidth: 112,
      sortable: true,
      renderCell: ({ row }) => (
        <TruncateAwareTooltip arrow>
          {formatRelativeTokenTime(row.created_at)}
        </TruncateAwareTooltip>
      ),
    },
    {
      field: "expires_at",
      headerName: "Expires",
      flex: 14,
      minWidth: 112,
      sortable: true,
      renderCell: ({ row }) => (
        <TruncateAwareTooltip arrow>
          {formatRelativeTokenTime(row.expires_at)}
        </TruncateAwareTooltip>
      ),
    },
    {
      field: "last_used_at",
      headerName: "Last Used",
      flex: 14,
      minWidth: 112,
      sortable: true,
      sortComparator: (v1, v2) => {
        if (v1 == null && v2 == null) return 0;
        if (v1 == null) return 1;
        if (v2 == null) return -1;
        return new Date(v1).getTime() - new Date(v2).getTime();
      },
      renderCell: ({ row }) =>
        row.last_used_at ? (
          <TruncateAwareTooltip arrow>
            {formatRelativeTokenTime(row.last_used_at)}
          </TruncateAwareTooltip>
        ) : (
          "Never"
        ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 10,
      minWidth: 88,
      sortable: true,
      valueGetter: (_value, row) => getTokenStatus(row),
      renderCell: ({ value }) => {
        const status = value as TokenStatus;
        const config = statusConfig[status];

        return (
          <Tooltip
            title={
              status === "revoked"
                ? "May take up to 30 seconds to take effect across all services"
                : ""
            }
            disableHoverListener={status !== "revoked"}
          >
            <div className="min-w-0 truncate">
              <Chip
                label={config.label}
                color={config.color}
                size="small"
                sx={chipEllipsisSx}
              />
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      width: 52,
      minWidth: 52,
      maxWidth: 52,
      sortable: false,
      disableReorder: true,
      align: "right",
      headerAlign: "right",
      // An expired token has no actions: it can't be revoked or deleted.
      renderCell: ({ row }) =>
        getTokenStatus(row) === "expired" ? null : (
          <IconButton
            size="small"
            onClick={(e) => onOpenMenu(e.currentTarget, row)}
            aria-label="Token actions"
            className="self-center"
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        ),
    },
  ];
}
